import type { MarketsApi } from "@/v2/api/metadata/markets";
import type { OutcomesApi } from "@/v2/api/metadata/outcomes";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { BackofficeContext } from "@/v2/framework/context/types";
import type { DialogConfig } from "@/v2/framework/dialog/types";
import {
  COLLAPSE_CONFIG_ID,
  COLLAPSE_STATUSES,
  displayIdRowFields,
  MARKET_OUTCOME_ORDER_CONFIG_ID,
  marketGamesStatusOptions,
  marketOutcomesActivityOptions,
  marketPositionValidator,
  marketStatusOptions,
  marketTypeOptions,
} from "../../shared";
import { buildAddMarketSupplementalNames } from "./market-fields";

export function createAddDialog(options: {
  ctx: BackofficeContext;
  marketsApi: MarketsApi;
  sportsApi: SportsApi;
}): DialogConfig {
  const { ctx, marketsApi, sportsApi } = options;

  return {
    id: "add-market",
    title: "Add market",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                {
                  type: "text-field",
                  name: "eventMarketName",
                  label: "Event market name",
                  required: true,
                  placeholder: "Enter event market name",
                },
                {
                  type: "select-field",
                  name: "sportId",
                  label: "Sport",
                  required: true,
                  placeholder: "Select sport",
                  options: async () => {
                    const sports = await sportsApi.getFilters({ page: 1, size: -1 });
                    return sports.map((sport) => ({
                      value: sport.id as string | number,
                      title: String(sport.computedName ?? sport.name ?? ""),
                    }));
                  },
                },
                {
                  type: "select-field",
                  name: "type",
                  label: "Market type",
                  required: true,
                  options: marketTypeOptions,
                  defaultValue: 1,
                },
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: marketStatusOptions,
                  defaultValue: 1,
                },
                { type: "text-field", name: "info", label: "Info", placeholder: "Enter info" },
                { type: "text-field", name: "eventType", label: "Event type" },
                { type: "text-field", name: "resultType", label: "Result type" },
                {
                  type: "select-field",
                  name: "marketOutcomesActivityDisplayType",
                  label: "Outcomes activity display",
                  options: marketOutcomesActivityOptions,
                },
                {
                  type: "component",
                  component: () => import("./MarketSupplementalNamesFields.vue"),
                  props: { mode: "add" },
                },
                {
                  type: "component",
                  component: () => import("./MarketSpecifiersFields.vue"),
                  props: { mode: "add", marketsApi },
                },
              ],
            },
            {
              type: "group",
              name: "prematchGame",
              label: "Pre-match",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: marketGamesStatusOptions,
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  validate: marketPositionValidator,
                },
                {
                  type: "structured-array-field",
                  name: "displayIds",
                  label: "Display IDs",
                  minItems: 0,
                  maxItems: 50,
                  children: displayIdRowFields,
                },
              ],
            },
            {
              type: "group",
              name: "liveGame",
              label: "Live",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: marketGamesStatusOptions,
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  validate: marketPositionValidator,
                },
                {
                  type: "structured-array-field",
                  name: "displayIds",
                  label: "Display IDs",
                  minItems: 0,
                  maxItems: 50,
                  children: displayIdRowFields,
                },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>;
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>;
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>;
        const applicationId = ctx.read.applicationId();
        const specifiers = Array.isArray(data.specifiers) ? data.specifiers : [];
        const competitors = Array.isArray(data.competitors) ? data.competitors : [];

        const model: Record<string, unknown> = {
          name: main.name,
          eventMarketName: main.eventMarketName,
          sportId: main.sportId,
          type: main.type,
          status: main.status,
          info: main.info,
          eventType: main.eventType,
          resultType: main.resultType,
          marketOutcomesActivityDisplayType: main.marketOutcomesActivityDisplayType,
          supplementalNames: buildAddMarketSupplementalNames(data),
          specifiers: specifiers.map((row) => {
            const specifier = row as Record<string, unknown>;
            const split = (value: unknown) =>
              String(value ?? "")
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
            return {
              ...specifier,
              validations: split(specifier.validations),
              defaultValues: split(specifier.defaultValues),
            };
          }),
          competitors,
          prematchGame: {
            status: prematchGame.status,
            position: prematchGame.position,
            displayIds: prematchGame.displayIds,
          },
          liveGame: {
            status: liveGame.status,
            position: liveGame.position,
            displayIds: liveGame.displayIds,
          },
        };

        if (applicationId) {
          model.applicationConfigurations = [
            {
              applicationId,
              configurationId: COLLAPSE_CONFIG_ID,
              configurationValue: JSON.stringify({
                [COLLAPSE_CONFIG_ID]: { status: COLLAPSE_STATUSES.EXPANDED },
              }),
            },
            {
              applicationId,
              configurationId: MARKET_OUTCOME_ORDER_CONFIG_ID,
              configurationValue: 1,
            },
          ];
        }

        return marketsApi.create(model);
      },
    },
  };
}
